import { useEffect, useState } from "react";
import { Plus, RefreshCw, RotateCcw, Trash2, ArrowUp, ArrowDown, Loader2, AlertTriangle,
  ChevronRight, ChevronsUpDown, ChevronsDownUp, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { estApi } from "../lib/api";
import AccelerationRow from "./AccelerationRow";

// "(x) mobilizations", "Lead time X weeks", "$XX,XXX", "#-# weeks" — anything still needing a
// real number before this goes to a customer.
const PLACEHOLDER = /\((?:x+|\d*x+)\)|\$?X{2,}|#\s*-\s*#|\bX\b(?=\s*(?:weeks?|days?|man|mobil))/i;
export const needsInput = (text) => PLACEHOLDER.test(text || "");

export default function QualificationsSection({ estimateId, readOnly = false, sections = [] }) {
  const [rows, setRows] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  // Scopes start closed so the estimate stays scannable; the header toggles them all.
  const [collapsed, setCollapsed] = useState([]);
  const [everOpened, setEverOpened] = useState(false);
  const [newText, setNewText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    estApi.getCostCodes().then((d) => setCostCodes(d.cost_codes || [])).catch(() => {});
  }, []);

  const codeName = Object.fromEntries(costCodes.map((c) => [c.code, c.name]));

  useEffect(() => {
    estApi.getQualifications(estimateId)
      .then((d) => setRows(d.qualifications || []))
      .finally(() => setLoaded(true));
  }, [estimateId]);

  const persist = async (next) => {
    setRows(next);
    const saved = await estApi.saveQualifications(estimateId, next);
    setRows(saved.qualifications || []);
  };

  const sync = async () => {
    setBusy(true);
    try {
      const res = await estApi.syncQualifications(estimateId);
      setRows(res.qualifications || []);
      toast.success(res.added
        ? `Added ${res.added} qualification(s) from the library`
        : "Nothing new to pull — already in sync");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const res = await estApi.resetQualifications(estimateId);
      setRows(res.qualifications || []);
      toast.success("Reset from the library (your manual entries were kept)");
    } finally {
      setBusy(false);
    }
  };

  const addManual = async () => {
    if (!newText.trim()) return;
    await persist([...rows, {
      id: crypto.randomUUID(), text: newText.trim(), source: "manual",
      library_qualification_id: null, scope_tag: "", cost_code: "", kind: "include",
      level: "project", section: "", included: true, sort_order: rows.length,
    }]);
    setNewText("");
  };

  const move = (index, dir) => {
    const next = [...rows];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    persist(next);
  };

  const included = rows.filter((r) => r.included).length;
  const openItems = rows.filter((r) => r.included && needsInput(r.text));

  // Grouped the way the proposal prints: project qualifications up top, then one block per cost
  // code scope with its inclusions and exclusions.
  const groups = (() => {
    const withIdx = rows.map((q, i) => ({ q, i }));
    const project = withIdx.filter(({ q }) => q.level !== "scope");
    const scoped = withIdx.filter(({ q }) => q.level === "scope");
    const byScope = new Map();
    scoped.forEach((row) => {
      const key = row.q.cost_code || row.q.section || row.q.scope_tag || "Other scopes";
      if (!byScope.has(key)) byScope.set(key, []);
      byScope.get(key).push(row);
    });
    const out = [];
    // Pinned first, even when empty: these print above the scope of work on every proposal.
    out.push({
      key: "__project", label: "Project Qualifications", pinned: true,
      includes: project.filter(({ q }) => q.kind !== "exclude"),
      excludes: project.filter(({ q }) => q.kind === "exclude"),
    });
    [...byScope.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([key, items]) => {
      out.push({
        key,
        label: codeName[key] ? `${key} — ${codeName[key]}` : key,
        includes: items.filter(({ q }) => q.kind !== "exclude"),
        excludes: items.filter(({ q }) => q.kind === "exclude"),
      });
    });
    return out;
  })();

  const groupKeys = groups.map((g) => g.key);
  const allCollapsed = groupKeys.length > 0 && groupKeys.every((k) => collapsed.includes(k));

  // Default state: every scope closed (project qualifications stay open).
  useEffect(() => {
    if (everOpened || !groupKeys.length) return;
    setCollapsed(groupKeys.filter((k) => k !== "__project"));
    setEverOpened(true);
  }, [groupKeys.join("|"), everOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid="qualifications-section">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-slate-800">Qualifications</h3>
        {groupKeys.length > 1 && (
          <button onClick={() => setCollapsed(allCollapsed ? [] : groupKeys)}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  title={allCollapsed ? "Expand all scopes" : "Collapse all scopes"}
                  data-testid="qual-toggle-all">
            {allCollapsed ? <ChevronsUpDown className="h-3.5 w-3.5" />
                          : <ChevronsDownUp className="h-3.5 w-3.5" />}
          </button>
        )}
        <span className="text-[11px] text-slate-400" data-testid="qual-count">
          {included} of {rows.length} on the proposal
        </span>
        {openItems.length > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                title="These still say (x), X weeks or $XX,XXX — edit them before sending"
                data-testid="qual-needs-input-count">
            <AlertTriangle className="h-3 w-3" /> {openItems.length} need a number
          </span>
        )}
        <div className={`ml-auto flex gap-1.5 ${readOnly ? "hidden" : ""}`}>
          <Button variant="outline" size="sm" className="text-xs" onClick={sync} disabled={busy}
                  data-testid="qual-sync-btn">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
            Pull from library
          </Button>
          <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={reset}
                  disabled={busy} data-testid="qual-reset-btn">
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
          </Button>
        </div>
      </div>

      <div data-testid="qual-list">
        <AccelerationRow estimateId={estimateId} readOnly={readOnly} />
        {loaded && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-xs text-slate-400">
            No qualifications yet. "Pull from library" adds the defaults plus anything matching the
            cost codes on this estimate.
          </p>
        )}
        {groups.map((g) => {
          const isOpen = !collapsed.includes(g.key);
          return (
          <div key={g.key} data-testid={`qual-group-${g.key}`}
               className={g.pinned ? "border-b-2 border-slate-200" : ""}>
            <button onClick={() => setCollapsed((c) =>
                      c.includes(g.key) ? c.filter((k) => k !== g.key) : [...c, g.key])}
                    className={`flex w-full items-center gap-2 px-4 py-1.5 text-left transition-colors ${
                      g.pinned ? "bg-slate-200/80 hover:bg-slate-300/60"
                               : "bg-slate-100 hover:bg-slate-200/70"}`}
                    data-testid={`qual-group-toggle-${g.key}`}>
              <ChevronRight className={`h-3 w-3 text-slate-400 transition-transform ${
                isOpen ? "rotate-90" : ""}`} />
              {g.pinned && <Lock className="h-3 w-3 text-slate-400" />}
              <span className="text-[11px] font-semibold text-slate-700">{g.label}</span>
              <span className="text-[10px] text-slate-400">
                {g.key === "__project"
                  ? `${g.includes.length + g.excludes.length} above the scope of work`
                  : `${g.includes.length} inclusions · ${g.excludes.length} exclusions`}
              </span>
              {[...g.includes, ...g.excludes].some(({ q }) => q.included && needsInput(q.text)) && (
                <AlertTriangle className="h-3 w-3 text-amber-500" />
              )}
            </button>
            {isOpen && g.pinned && !g.includes.length && !g.excludes.length && (
              <p className="px-4 py-3 text-[11px] text-slate-400" data-testid="qual-project-empty">
                Nothing project-level yet. Anything added here prints above the scope of work on
                every proposal — set the placement dropdown on a row to "Project" to move it up here.
              </p>
            )}
            {isOpen && [["Scope Inclusions", g.includes], ["Scope Exclusions", g.excludes]].map(
              ([label, items]) => items.length ? (
                <div key={label}>
                  {g.key !== "__project" && (
                    <div className="bg-slate-50 px-4 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {label}
                    </div>
                  )}
                  <div className="divide-y divide-slate-100">
                    {items.map(({ q, i }) => (
          <div key={q.id} className={`flex items-start gap-2 px-4 py-2 ${
            q.included && needsInput(q.text) ? "bg-amber-50/60" : ""}`}
               data-testid={`qual-row-${q.id}`}>
            <input type="checkbox" checked={!!q.included} className="mt-1" disabled={readOnly}
                   onChange={() => persist(rows.map((r) =>
                     r.id === q.id ? { ...r, included: !r.included } : r))}
                   data-testid={`qual-include-${q.id}`} />
            <div className="flex-1">
              <textarea
                value={q.text} rows={Math.min(4, Math.ceil((q.text || "").length / 110) || 1)}
                onChange={(e) => setRows(rows.map((r) => r.id === q.id ? { ...r, text: e.target.value } : r))}
                onBlur={() => !readOnly && persist(rows)}
                readOnly={readOnly}
                className={`w-full resize-y rounded border px-1.5 py-1 text-xs leading-relaxed focus:border-[#007BFF] focus:outline-none ${
                  q.included && needsInput(q.text)
                    ? "border-amber-300 bg-white" : "border-transparent hover:border-slate-200"} ${
                  q.included ? "text-slate-700" : "text-slate-400 line-through"}`}
                data-testid={`qual-text-${q.id}`}
              />
              {q.included && needsInput(q.text) && (
                <span className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-amber-700"
                      data-testid={`qual-needs-input-${q.id}`}>
                  <AlertTriangle className="h-2.5 w-2.5" /> Fill in the number before sending
                </span>
              )}
            </div>
            <select
              value={q.level === "scope" ? (q.section || q.cost_code || "__tag") : "__project"}
              disabled={readOnly}
              onChange={(e) => {
                const v = e.target.value;
                persist(rows.map((r) => r.id === q.id ? {
                  ...r,
                  level: v === "__project" ? "project" : "scope",
                  section: v === "__project" || v === "__tag" ? "" : v,
                } : r));
              }}
              className="mt-0.5 h-6 shrink-0 rounded border border-slate-200 bg-white px-1 text-[10px] text-slate-500"
              data-testid={`qual-placement-${q.id}`}>
              <option value="__project">Project</option>
              {q.cost_code && <option value={q.cost_code}>{q.cost_code}</option>}
              {q.scope_tag && <option value="__tag">All {q.scope_tag} scopes</option>}
              {sections.map((sec) => <option key={sec} value={sec}>{sec}</option>)}
            </select>
            <select
              value={q.kind === "exclude" ? "exclude" : "include"}
              disabled={readOnly}
              onChange={(e) => persist(rows.map((r) =>
                r.id === q.id ? { ...r, kind: e.target.value } : r))}
              className="mt-0.5 h-6 shrink-0 rounded border border-slate-200 bg-white px-1 text-[10px] text-slate-500"
              data-testid={`qual-kind-${q.id}`}>
              <option value="include">Inclusion</option>
              <option value="exclude">Exclusion</option>
            </select>
            <div className={`mt-0.5 flex shrink-0 gap-0.5 ${readOnly ? "hidden" : ""}`}>
              <button onClick={() => move(i, -1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"
                      data-testid={`qual-up-${q.id}`}><ArrowUp className="h-3 w-3" /></button>
              <button onClick={() => move(i, 1)} className="rounded p-1 text-slate-400 hover:bg-slate-100"
                      data-testid={`qual-down-${q.id}`}><ArrowDown className="h-3 w-3" /></button>
              <button onClick={() => persist(rows.filter((r) => r.id !== q.id))}
                      className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                      data-testid={`qual-delete-${q.id}`}><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
                    ))}
                  </div>
                </div>
              ) : null)}
          </div>
          );
        })}
      </div>

      <div className={`flex gap-2 border-t border-slate-200 px-4 py-2.5 ${
        readOnly ? "hidden" : ""}`}>
        <Input value={newText} onChange={(e) => setNewText(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && addManual()}
               placeholder="Add a project-specific qualification…"
               className="h-8 text-xs" data-testid="qual-new-input" />
        <Button size="sm" className="text-xs" onClick={addManual} data-testid="qual-add-btn">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add
        </Button>
      </div>
    </section>
  );
}
